import type { AgentRef } from "@/components/AgentChatProvider";

/** The link scheme linkRefs writes; AgentChatMessage routes it in-app instead of navigating. */
export const REF_SCHEME = "dolfin-ref:";

function escapeRegExp(text: string) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Links whole-word mentions of each ref's label, longest first, so INV-0011 is never half-linked as INV-001.
 * shortcut: only inline code directly around the label is detected, not a fenced block; upgrade to a remark
 * plugin over text nodes if the agent starts writing invoice numbers inside code blocks.
 */
export function linkRefs(content: string, refs: AgentRef[] | undefined): string {
  if (!refs || refs.length === 0 || !content) return content;
  const byLabel = new Map<string, AgentRef | null>();
  for (const ref of refs) {
    const seen = byLabel.get(ref.label);
    // A label two different records share (two suppliers' INV-001, or a bill and an invoice) is ambiguous: left
    // unlinked rather than risk opening the wrong record. The same record named twice still links.
    byLabel.set(ref.label, seen === undefined || (seen && seen.type === ref.type && seen.id === ref.id) ? ref : null);
  }
  if (![...byLabel.values()].some(Boolean)) return content;
  // Ambiguous labels stay in the pattern and match as plain text, so a shorter label inside one (the merchant in
  // "Pret A Manger £4.50") isn't linked in its place.
  const labels = [...byLabel.keys()].sort((a, b) => b.length - a.length).map(escapeRegExp);
  const pattern = new RegExp(`(?<![\\w\\[/\`-])(${labels.join("|")})(?![\\w\`-]|\\]\\()`, "g");
  return content.replace(pattern, (label) => {
    const ref = byLabel.get(label);
    return ref ? `[${label}](${REF_SCHEME}${encodeURIComponent(ref.type)}/${encodeURIComponent(ref.id)})` : label;
  });
}
